import React, { Suspense, lazy } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  BookOpen,
  UserCheck,
  Users,
  UserCog,
  GraduationCap,
  Layers,
  Globe,
  CalendarCheck,
  Newspaper,
  Plus,
  Award,
  User,
  Settings,
  Briefcase,
  MessageCircle,
  Calendar,
  Info,
  Phone,
  BarChart2,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useUIStore } from '../../store/useUIStore';
import { getNavGroupsForRole, getDisplayRole, getProfilePath } from '../../config/navigation';
import { Avatar } from '../ui/Avatar';

const LogOut = lazy(() => import('lucide-react').then((m) => ({ default: m.LogOut })));

// Map string icon names to actual LucideIcon components
const iconMap: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  LayoutDashboard,
  BookOpen,
  UserCheck,
  Users,
  UserCog,
  GraduationCap,
  Layers,
  Globe,
  CalendarCheck,
  Newspaper,
  Plus,
  Award,
  User,
  Settings,
  Briefcase,
  MessageCircle,
  Calendar,
  Info,
  Phone,
  BarChart2,
};

const Sidebar: React.FC = () => {
  const { user, logoutUser } = useAuth();
  const {
    isSidebarCollapsed,
    toggleSidebarCollapsed,
    isMobileSidebarOpen,
    closeMobileSidebar,
  } = useUIStore();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logoutUser();
    navigate('/login');
  };

  const isActive = (path: string) => location.pathname === path;

  // Get nav groups from shared config
  const role = user?.isAdmin ? 'admin' : user?.role || 'student';
  const navGroups = getNavGroupsForRole(role as 'student' | 'mentor' | 'admin', user?.isAdmin || false);
  const profilePath = user ? getProfilePath(user.role, user.isAdmin) : '/dashboard/student/profile';
  const effectiveRole = user?.isAdmin ? 'Admin' : getDisplayRole(user?.role || 'student');

  return (
    <>
      {/* Mobile Overlay */}
      <div
        className={`fixed inset-0 bg-black/60 z-40 transition-opacity md:hidden ${
          isMobileSidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeMobileSidebar}
        aria-hidden="true"
      />

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full bg-bg-primary border-r border-border-default z-50 flex flex-col transition-all duration-300 ease-out
        ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'} ${
          isSidebarCollapsed ? 'w-20' : 'w-64'
        } md:relative md:h-full md:shrink-0 md:translate-x-0`}
        aria-label="Main navigation"
      >
        {/* Sidebar Header */}
        <div
          className={`flex h-16 shrink-0 items-center ${
            isSidebarCollapsed ? 'justify-center' : 'justify-between'
          } px-4 border-b border-border-default`}
        >
          {!isSidebarCollapsed && (
            <Link to="/" className="flex items-center gap-3" aria-label="ShreeGuruCool Home">
              <img src="/logo.jpeg" alt="SGC" className="h-9 w-9 rounded-full" />
              <span className="text-heading-sm font-bold text-text-primary">ShreeGuruCool</span>
            </Link>
          )}
          <button
            onClick={toggleSidebarCollapsed}
            className="p-2 rounded-lg text-text-secondary hover:bg-bg-tertiary hover:text-text-primary transition-colors hidden md:flex"
            aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!isSidebarCollapsed}
          >
            <LayoutDashboard className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto px-2 py-4" aria-label="Dashboard navigation">
          <Suspense fallback={<div className="px-4 py-2 text-text-muted text-sm">Loading...</div>}>
            {navGroups.map((group, groupIndex) => (
              <div key={groupIndex} className="space-y-1">
                {group.label && !isSidebarCollapsed && (
                  <div className="px-3 py-2 text-caption font-semibold text-text-muted uppercase tracking-wider">
                    {group.label}
                  </div>
                )}
                {group.items.map((item) => {
                  const Icon = iconMap[item.icon] || LayoutDashboard;
                  const active = isActive(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`relative group flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-fast
                        ${active
                          ? 'bg-primary-50 text-primary-700 font-medium dark:bg-primary-900/30 dark:text-primary-400'
                          : 'text-text-secondary hover:bg-bg-tertiary hover:text-text-primary'
                        }`}
                      onClick={closeMobileSidebar}
                      aria-current={active ? 'page' : undefined}
                    >
                      <div className="flex-shrink-0" aria-hidden="true">
                        <Icon className="h-5 w-5" />
                      </div>
                      {!isSidebarCollapsed && (
                        <span className="text-body-sm truncate">{item.name}</span>
                      )}
                      {isSidebarCollapsed && (
                        <span
                          className="absolute left-full ml-3 px-2 py-1.5 text-caption font-medium text-text-inverse bg-text-primary rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10 pointer-events-none"
                        >
                          {item.name}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            ))}
          </Suspense>
        </nav>

        {/* User Card & Logout */}
        <div className="shrink-0 border-t border-border-default px-4 py-3">
          {user && !isSidebarCollapsed && (
            <Link
              to={profilePath}
              className="flex items-center gap-3 p-2 bg-bg-tertiary rounded-lg mb-3 hover:bg-border-subtle transition-colors"
            >
              <Avatar src={user.avatar} name={user.name} alt="" size="sm" />
              <div className="truncate flex-1">
                <p className="font-medium text-sm text-text-primary">{user.name}</p>
                <p className="text-caption text-text-muted capitalize">{effectiveRole}</p>
              </div>
            </Link>
          )}

          <button
            onClick={handleLogout}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-danger hover:bg-danger/10 hover:text-danger transition-colors ${
              isSidebarCollapsed ? 'justify-center' : ''
            }`}
            aria-label="Logout"
          >
            <Suspense fallback={<div className="w-5 h-5" />}>
              <LogOut size={18} />
            </Suspense>
            {!isSidebarCollapsed && <span className="text-body-sm font-medium">Logout</span>}
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
